import React, { useRef } from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import LoadingButton from '../common/LoadingButton';
import TextField from '../common/NewTextField';

const useStyles = makeStyles(() => ({
  buttonsContainer: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 24,
    width: '100%',
  },
  cancelButton: {
    borderRadius: '4px',
    color: '#42526E',
    flex: '1',
    fontWeight: '500',
    textTransform: 'none',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflow: 'hidden',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 12,
    marginRight: '-16px',
    marginTop: 16,
    overflowY: 'auto',
    paddingRight: 16,
    width: 'min(100%, 400px)',
  },
  label: {
    fontWeight: 'bold',
  },
  submitButton: {
    '&:hover': {
      backgroundColor: '#2D95C4',
    },
    backgroundColor: '#36B2EC',
    borderRadius: 4,
    color: 'white',
    flex: '1',
    fontWeight: 500,
    textTransform: 'none',
  },
  subtitle: {
    color: '#6B778C',
    fontSize: 14,
    textAlign: 'center',
  },
  title: {
    color: '#172B4D',
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    textAlign: 'center',
  },
}));

const AddFacilityForm = ({
  onAddFacility,
  defaultData = {},
  isSubmitting,
  onCancel,
  title = 'Add New Facility',
  subtitle = 'Add a new medical facility to manage departments and locations',
  isEditing = false,
}) => {
  const classes = useStyles();

  // Field refs
  const nameRef = useRef();
  const codeRef = useRef();
  const addressRef = useRef();

  const onAddClick = () => {
    const name = nameRef.current?.getValue();
    const code = codeRef.current?.getValue();
    const address = addressRef.current?.getValue();

    nameRef.current?.clearError();
    codeRef.current?.clearError();
    addressRef.current?.clearError();

    let hasErrors = false;

    if (!name?.trim()) {
      nameRef.current?.setError('Facility name is required');
      hasErrors = true;
    }

    if (!hasErrors) {
      const validData = {
        address: address?.trim() || null,
        code: code?.trim() || null,
        name: name.trim(),
      };
      onAddFacility(validData);
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            {title}
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            {subtitle}
          </Typography>
        </div>

        <div className={classes.formContainer}>
          <Box>
            <Typography className={classes.label}>Facility Name</Typography>
            <TextField ref={nameRef} name="name" placeholder="Enter facility name" required value={defaultData?.name || ''} />
          </Box>

          <Box>
            <Typography className={classes.label}>Facility Code</Typography>
            <TextField ref={codeRef} name="code" placeholder="Enter facility code" value={defaultData?.code || ''} />
          </Box>

          <Box>
            <Typography className={classes.label}>Address</Typography>
            <TextField ref={addressRef} name="address" placeholder="Enter complete address" multiline rows={3} value={defaultData?.address || ''} />
          </Box>
        </div>

        <div className={classes.buttonsContainer}>
          <Button variant="outlined" onClick={onCancel} className={classes.cancelButton}>
            Cancel
          </Button>
          <LoadingButton variant="contained" onClick={onAddClick} loading={isSubmitting} className={classes.submitButton}>
            {isEditing ? 'Update Facility' : 'Add Facility'}
          </LoadingButton>
        </div>
      </div>
    </>
  );
};

export default AddFacilityForm;
